import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  Divider,
  styled,
  TooltipProps,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCurrencyCode } from "../../genericComponents/helper-functions/CurrencySymbol";
import ToolTipSafetyInfo from "./ToolTipSafetyInfo";
import { PlansPayments } from "../../plans/PlansPayment";

interface BottomDrawerProps {
  open: any;
  close: any;
  orderSummary: any;
  setReloadFlagAfterSubsciption: any;
}

const BottomDrawer: React.FC<BottomDrawerProps> = ({
  open,
  close,
  orderSummary,
  setReloadFlagAfterSubsciption,
}) => {
  const currencyCode = useCurrencyCode();
  const [openToolTipInfo, setOpenToolTipInfo] = useState(false);
  const [openPlans, setOpenPlans] = React.useState(false);

  const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: "#15741a",
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#15741a",
      color: "white",
    },
  }));

  return (
    <Drawer
      open={open}
      onClose={close}
      anchor="bottom"
      sx={{
        display: { xs: "block", sm: "none" },
        zIndex: 10000,
        "& .MuiDrawer-paper": {
          background: "#f5f5f5",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          width: "100%",
          padding: "1rem",
        },
      }}
      PaperProps={{
        sx: {
          height: "auto",
          padding: "1rem",
          maxWidth: "100%",
        },
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Payment Summary
        </Typography>
        <IconButton onClick={close} sx={{ color: "black" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          background: "#fff",
          padding: "10px",
          borderRadius: "8px",
          marginTop: "1rem",
          marginBottom: "5rem",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "2px 0rem 5px",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
            MRP Price
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            {currencyCode}
            {orderSummary?.totalMrp}
          </Typography>
        </Box>
        {orderSummary?.totalAmount && orderSummary?.totalAmount > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Total Amount
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary?.totalAmount}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {orderSummary?.walletRedeem && orderSummary?.walletRedeem > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Wallet redeem
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              - {currencyCode}
              {orderSummary?.walletRedeem}
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {orderSummary?.primeTimeCharge && orderSummary?.primeTimeCharge > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Prime time charges
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary?.primeTimeCharge}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {orderSummary?.disposableKitAmount &&
        orderSummary?.disposableKitAmount > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Safety & Hygiene Kit &nbsp;
              <BootstrapTooltip title="Information" placement="top">
                <InfoOutlinedIcon
                  sx={{
                    color: "#000",
                    cursor: "pointer",
                    fontSize: "19px",
                  }}
                  onClick={() => setOpenToolTipInfo(true)}
                />
              </BootstrapTooltip>
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary?.disposableKitAmount}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {orderSummary?.cancellationCharges &&
        orderSummary?.cancellationCharges > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Cancellation Charges
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary?.cancellationCharges}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {orderSummary?.couponDiscount && orderSummary?.couponDiscount > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Coupon discount
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              - {currencyCode}
              {orderSummary?.couponDiscount}
            </Typography>
          </Box>
        ) : (
          <></>
        )}

        {orderSummary?.transportationCharge &&
        orderSummary?.transportationCharge > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "2px 0rem 5px",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "500" }}>
              Transportation charges
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              {currencyCode}
              {orderSummary?.transportationCharge}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
        {!orderSummary?.customerSubscribed ? (
          <Box
            sx={{
              border: "1px solid #ffb600",
              borderRadius: "8px",
              padding: "8px",
              margin: "1rem auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  color: "#000",
                }}
                variant="subtitle2"
              >
                Subscription Discount
              </Typography>
              <Typography
                sx={{
                  fontWeight: "bold",
                  color: "#000",
                }}
                variant="subtitle2"
              >
                - {currencyCode}
                {orderSummary?.subscriptionDiscount}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "left",
                alignItems: "center",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#000",
                  marginTop: "5px",
                }}
              >
                Get Extra {currencyCode} {orderSummary?.subscriptionDiscount}{" "}
                OFF
              </Typography>{" "}
              &nbsp; &nbsp;
              <Typography
                variant="subtitle2"
                sx={{
                  color: "#000",
                  background: "#ffb600",
                  borderRadius: "10rem",
                  padding: "1px 12px",
                }}
                onClick={() => setOpenPlans(true)}
              >
                Apply Now
              </Typography>
            </Box>
          </Box>
        ) : (
          ""
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "2px 0rem 5px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "bold", color: "green" }}
          >
            Total discount
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: "bold", color: "green" }}
          >
            - {currencyCode}
            {orderSummary?.totalDiscount}
          </Typography>
        </Box>

        <Divider />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "10px 0rem",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
            Amount Payable
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {currencyCode}
            {orderSummary?.finalAmount}
          </Typography>
        </Box>
      </Box>
      <ToolTipSafetyInfo
        openInfo={openToolTipInfo}
        handleClose={() => setOpenToolTipInfo(false)}
      />
      <PlansPayments
        openPlans={openPlans}
        closePlans={() => {
          setOpenPlans(false);
        }}
        reloadFlagAfterSubsciption={() => setReloadFlagAfterSubsciption(true)}
      />
    </Drawer>
  );
};

interface PaymentSummeryMobileProps {
  drawerOpen: any;
  closeDrawer: any;
  orderSummary: any;
  setReloadFlagAfterSubsciption: any;
}

const PaymentSummeryMobile: React.FC<PaymentSummeryMobileProps> = ({
  drawerOpen,
  closeDrawer,
  orderSummary,
  setReloadFlagAfterSubsciption,
}) => {
  return (
    <div>
      <BottomDrawer
        open={drawerOpen}
        close={closeDrawer}
        orderSummary={orderSummary}
        setReloadFlagAfterSubsciption={setReloadFlagAfterSubsciption}
      />
    </div>
  );
};

export default PaymentSummeryMobile;
